// Shared geolocation module for WIN Field App v2 (card #4833).
//
// Replaces the old "request a fix on every page load / Turbo navigation"
// behaviour (field_geolocation_controller.js used to call getCurrentPosition
// on every connect(), ~86 requests/tasker/day in production) with requests
// made ONLY at the moments the card calls out:
//   (a) places list "Nearest" sort and the map view (place_sort_controller.js,
//       field_places_map_controller.js)
//   (b) response submission, and response start if a fix is already fresh
//       (surveys/result_editor_controller.js)
//   (c) shift start and end -- unchanged, see shifts_controller.rb
//   (d) a sync pass that actually has something to send, while online
//       (field_sync_controller.js)
//
// Two reuse windows: a fix under 10 minutes old is good enough for the places
// list/map (MAP_MAX_AGE_MS); a submission wants something fresher -- under 2
// minutes old is reused as-is (with its age recorded), anything older gets
// one fresh request (SUBMISSION_MAX_AGE_MS).
//
// Denial memory (card #4833, replacing #1900's 30-minute TTL marker): a
// PERMISSION_DENIED result sets a persistent (no expiry) local flag. Every
// call in this module checks that flag before ever touching
// navigator.geolocation again, so a tasker who said no is never re-prompted
// automatically. Because an iPhone can report "prompt" again from
// navigator.permissions.query after a real denial (our own flag is the
// trustworthy source, not a single live read), the flag is cleared only by
// an explicit Tasker action (field_settings_controller.js's manual "Enable
// location" retry) or by navigator.permissions.query later reporting
// "granted" (self-heals the next time this module runs, and
// field_geolocation_controller.js watches onchange for the same signal).
//
// PRIVACY: never log a coordinate. logEvent/logWarn calls below only ever
// carry accuracy (metres) and elapsed time -- the same discipline the old
// controller followed.

import { logEvent, logWarn } from "./field_logger"

export const LOCATION_KEY = "field_user_location"
export const DENIED_KEY   = "field_geolocation_denied"

const MAP_MAX_AGE_MS        = 10 * 60 * 1000 // places list / map reuse window
const SUBMISSION_MAX_AGE_MS = 2  * 60 * 1000 // submission reuse window
const FIX_TIMEOUT_MS        = 10_000

function readCached() {
  try { return JSON.parse(localStorage.getItem(LOCATION_KEY)) } catch { return null }
}

function writeCached(fix) {
  try { localStorage.setItem(LOCATION_KEY, JSON.stringify(fix)) } catch { /* private window / full storage */ }
}

function ageMs(fix) {
  return fix?.timestamp ? Date.now() - fix.timestamp : Infinity
}

// The last fix this device obtained, however it got there (map, settings,
// submission, sync refresh) -- all paths share this one cache.
export function cachedLocation() {
  return readCached()
}

export function hasFreshFix(maxAgeMs = MAP_MAX_AGE_MS) {
  return ageMs(readCached()) < maxAgeMs
}

export function isDeniedLocally() {
  try { return localStorage.getItem(DENIED_KEY) === "1" } catch { return false }
}

export function recordDenial() {
  try { localStorage.setItem(DENIED_KEY, "1") } catch { /* ignore */ }
}

export function clearDenial() {
  try { localStorage.removeItem(DENIED_KEY) } catch { /* ignore */ }
}

// True when a fresh request should be skipped because the Tasker already
// said no. Trusts our own memory first; if the browser's own Permissions API
// now says "granted" (the Tasker fixed it in Settings since our flag was
// set), self-heals immediately rather than waiting for a manual retry.
export async function isBlocked() {
  if (!isDeniedLocally()) return false
  if (!("permissions" in navigator)) return true
  try {
    const result = await navigator.permissions.query({ name: "geolocation" })
    if (result.state === "granted") {
      clearDenial()
      return false
    }
    return true
  } catch {
    return true
  }
}

function requestFix({ enableHighAccuracy = false, timeout = FIX_TIMEOUT_MS } = {}) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return }
    const start = performance.now()
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const fix = {
          lat:       pos.coords.latitude,
          lng:       pos.coords.longitude,
          accuracy:  pos.coords.accuracy,
          timestamp: Date.now()
        }
        writeCached(fix)
        clearDenial()
        logEvent("geolocation", "Location acquired", {
          accuracy: Math.round(pos.coords.accuracy),
          elapsed:  Math.round(performance.now() - start)
        }).catch(() => {})
        resolve(fix)
      },
      (err) => {
        const reason = err.code === err.PERMISSION_DENIED ? "denied"
                     : err.code === err.TIMEOUT           ? "timeout"
                     :                                      "unavailable"
        if (err.code === err.PERMISSION_DENIED) recordDenial()
        logWarn("geolocation", `Location failed: ${reason}`, {
          code: err.code, elapsed: Math.round(performance.now() - start)
        }).catch(() => {})
        resolve(null)
      },
      { enableHighAccuracy, timeout, maximumAge: 0 }
    )
  })
}

// Moment (a) -- places "Nearest" sort and the map view. Reuses a fix under 10
// minutes old (no browser call at all); otherwise asks once. Never throws --
// a denial or timeout simply resolves with whatever's cached (possibly null),
// so a caller never has to special-case failure.
export async function getLocationForMap() {
  const cached = readCached()
  if (ageMs(cached) < MAP_MAX_AGE_MS) return cached
  if (await isBlocked()) return cached ?? null
  const fresh = await requestFix()
  return fresh ?? cached ?? null
}

// Moment (b) -- response start/submission. A fix under 2 minutes old is
// reused, with its age reported alongside (fixAgeSeconds); anything older
// triggers one fresh, bounded request. Resolves null on denial/timeout/no
// support -- callers must never let that block the submission itself.
export async function getLocationForSubmission() {
  const cached = readCached()
  const age = ageMs(cached)
  if (age < SUBMISSION_MAX_AGE_MS) {
    return { lat: cached.lat, lng: cached.lng, accuracy: cached.accuracy, fixAgeSeconds: Math.round(age / 1000) }
  }
  if (await isBlocked()) return null
  const fresh = await requestFix()
  if (!fresh) return null
  return { lat: fresh.lat, lng: fresh.lng, accuracy: fresh.accuracy, fixAgeSeconds: 0 }
}

// Moment (d) -- an online sync pass that actually has something to send.
// Best-effort cache refresh only: callers never await this for correctness,
// just so the NEXT map/submission read finds something recent. Skips
// entirely when there's already a fix under 10 minutes old or a remembered
// denial.
export async function refreshLocationForSync() {
  if (hasFreshFix(MAP_MAX_AGE_MS)) return
  if (await isBlocked()) return
  await requestFix()
}
